import "@/App.css";
import NewsletterPage from './pages/NewsletterPage';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import setupAxiosInterceptors from "@/utils/axiosInterceptor";
import Dashboard from "@/pages/Dashboard";
import DashboardNew from "@/pages/DashboardNew";
import RegisterMember from "@/pages/RegisterMember";
import SearchMember from "@/pages/SearchMember";
import MemberReports from "@/pages/MemberReports";
import PrintLabel from "@/pages/PrintLabel";
import RegisterAdvertiser from "@/pages/RegisterAdvertiser";
import SearchAdvertiser from "@/pages/SearchAdvertiser";
import AdvertiserReports from "@/pages/AdvertiserReports";
import PrintAdvertiserLabel from "@/pages/PrintAdvertiserLabel";
import ScanDispatch from "@/pages/ScanDispatch";
import ScanDispatchMobile from "@/pages/ScanDispatchMobile";
import MarkFailedCourier from "@/pages/MarkFailedCourier";
import DispatchReports from "@/pages/DispatchReports";
import DispatchConfirmation from "@/pages/DispatchConfirmation";
import CourierExport from "@/pages/CourierExport";
import EnterBill from "@/pages/EnterBill";
import BillReports from "@/pages/BillReports";
import PrintBill from "@/pages/PrintBill";
import PrintBillNew from "@/pages/PrintBillNew";
import Login from "@/pages/Login";
import ImportData from "@/pages/ImportData";
import EmailSettings from "@/pages/EmailSettings";
import EmailTemplates from "@/pages/EmailTemplates";
import SendCampaign from "@/pages/SendCampaign";
import CampaignReports from "@/pages/CampaignReports";
import CreateProformaInvoice from "@/pages/CreateProformaInvoice";
import ProformaInvoiceReports from "@/pages/ProformaInvoiceReports";
import ExportData from "@/pages/ExportData";
import ClearData from "@/pages/ClearData";
import ManageUsers from "@/pages/ManageUsers";
import ChangeMyPassword from "@/pages/ChangeMyPassword";
import MigrateDates from "@/pages/MigrateDates";
import SessionLogs from "@/pages/SessionLogs";
import RenewalReminders from "@/pages/RenewalReminders";
import EmailReports from "@/pages/EmailReports";
import MonthlyMagazineEmail from "@/pages/MonthlyMagazineEmail";
import AdvertisementProposal from "@/pages/AdvertisementProposal";
import AdProposalReports from "@/pages/AdProposalReports";
import ProposalDrafts from "@/pages/ProposalDrafts";
import GreetingsEmail from "@/pages/GreetingsEmail";
import NewsBroadcast from "@/pages/NewsBroadcast";
import WhatsAppBroadcast from "@/pages/WhatsAppBroadcast";
import WhatsAppMagazineBroadcast from "@/pages/WhatsAppMagazineBroadcast";
import WhatsAppTemplateSend from "@/pages/WhatsAppTemplateSend";
import WhatsAppOptIn from "@/pages/WhatsAppOptIn";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import WhatsAppReports from "@/pages/WhatsAppReports";
import WhatsAppRenewal from "@/pages/WhatsAppRenewal";
import DeliveryFailures from "@/pages/DeliveryFailures";
import MigrateWhatsApp from "@/pages/MigrateWhatsApp";
import DataExport from "@/pages/DataExport";
import DataHealthCheck from "@/pages/DataHealthCheck";
import TariffManagement from "@/pages/TariffManagement";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Setup axios interceptors for auto-logout on 401
setupAxiosInterceptors();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/" element={<ProtectedRoute><DashboardNew /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardNew /></ProtectedRoute>} />
          <Route path="/dashboard-old" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/register" element={<ProtectedRoute><RegisterMember /></ProtectedRoute>} />
          <Route path="/search" element={<ProtectedRoute><SearchMember /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><MemberReports /></ProtectedRoute>} />
          <Route path="/print-label" element={<ProtectedRoute><PrintLabel /></ProtectedRoute>} />
          <Route path="/register-advertiser" element={<ProtectedRoute><RegisterAdvertiser /></ProtectedRoute>} />
          <Route path="/search-advertiser" element={<ProtectedRoute><SearchAdvertiser /></ProtectedRoute>} />
          <Route path="/advertiser-reports" element={<ProtectedRoute><AdvertiserReports /></ProtectedRoute>} />
          <Route path="/print-advertiser-label" element={<ProtectedRoute><PrintAdvertiserLabel /></ProtectedRoute>} />
          <Route path="/scan-dispatch" element={<ProtectedRoute><ScanDispatch /></ProtectedRoute>} />
          <Route path="/scan-dispatch-mobile" element={<ProtectedRoute><ScanDispatchMobile /></ProtectedRoute>} />
          <Route path="/mark-failed-courier" element={<ProtectedRoute><MarkFailedCourier /></ProtectedRoute>} />
          <Route path="/dispatch-reports" element={<ProtectedRoute><DispatchReports /></ProtectedRoute>} />
          <Route path="/dispatch-confirmation" element={<ProtectedRoute><DispatchConfirmation /></ProtectedRoute>} />
          <Route path="/courier-export" element={<ProtectedRoute><CourierExport /></ProtectedRoute>} />
          <Route path="/enter-bill" element={<ProtectedRoute><EnterBill /></ProtectedRoute>} />
          <Route path="/bill-reports" element={<ProtectedRoute><BillReports /></ProtectedRoute>} />
          <Route path="/print-bill" element={<ProtectedRoute><PrintBill /></ProtectedRoute>} />
          <Route path="/print-bill-new" element={<ProtectedRoute><PrintBillNew /></ProtectedRoute>} />
          <Route path="/import-data" element={<ProtectedRoute><ImportData /></ProtectedRoute>} />
          <Route path="/export-data" element={<ProtectedRoute><ExportData /></ProtectedRoute>} />
          <Route path="/clear-data" element={<ProtectedRoute><ClearData /></ProtectedRoute>} />
          <Route path="/manage-users" element={<ProtectedRoute><ManageUsers /></ProtectedRoute>} />
          <Route path="/change-my-password" element={<ProtectedRoute><ChangeMyPassword /></ProtectedRoute>} />
          <Route path="/migrate-dates" element={<ProtectedRoute><MigrateDates /></ProtectedRoute>} />
          <Route path="/session-logs" element={<ProtectedRoute><SessionLogs /></ProtectedRoute>} />
          <Route path="/email-settings" element={<ProtectedRoute><EmailSettings /></ProtectedRoute>} />
          <Route path="/email-templates" element={<ProtectedRoute><EmailTemplates /></ProtectedRoute>} />
          <Route path="/send-campaign" element={<ProtectedRoute><SendCampaign /></ProtectedRoute>} />
          <Route path="/newsletter" element={<ProtectedRoute><NewsletterPage /></ProtectedRoute>} />
          <Route path="/campaign-reports" element={<ProtectedRoute><CampaignReports /></ProtectedRoute>} />
          <Route path="/create-proforma-invoice" element={<ProtectedRoute><CreateProformaInvoice /></ProtectedRoute>} />
          <Route path="/proforma-invoice-reports" element={<ProtectedRoute><ProformaInvoiceReports /></ProtectedRoute>} />
          <Route path="/renewal-reminders" element={<ProtectedRoute><RenewalReminders /></ProtectedRoute>} />
          <Route path="/email-reports" element={<ProtectedRoute><EmailReports /></ProtectedRoute>} />
          <Route path="/monthly-magazine-email" element={<ProtectedRoute><MonthlyMagazineEmail /></ProtectedRoute>} />
          <Route path="/advertisement-proposal" element={<ProtectedRoute><AdvertisementProposal /></ProtectedRoute>} />
          <Route path="/ad-proposal-reports" element={<ProtectedRoute><AdProposalReports /></ProtectedRoute>} />
          <Route path="/proposal-drafts" element={<ProtectedRoute><ProposalDrafts /></ProtectedRoute>} />
          <Route path="/greetings-email" element={<ProtectedRoute><GreetingsEmail /></ProtectedRoute>} />
          <Route path="/news-broadcast" element={<ProtectedRoute><NewsBroadcast /></ProtectedRoute>} />
          <Route path="/whatsapp-broadcast" element={<ProtectedRoute><WhatsAppBroadcast /></ProtectedRoute>} />
          <Route path="/whatsapp-magazine-broadcast" element={<ProtectedRoute><WhatsAppMagazineBroadcast /></ProtectedRoute>} />
          <Route path="/whatsapp-template-send" element={<ProtectedRoute><WhatsAppTemplateSend /></ProtectedRoute>} />
          <Route path="/whatsapp-renewal" element={<ProtectedRoute><WhatsAppRenewal /></ProtectedRoute>} />
          <Route path="/whatsapp-optin" element={<ProtectedRoute><WhatsAppOptIn /></ProtectedRoute>} />
          <Route path="/whatsapp-inbox" element={<ProtectedRoute><WhatsAppInbox /></ProtectedRoute>} />
          <Route path="/whatsapp-reports" element={<ProtectedRoute><WhatsAppReports /></ProtectedRoute>} />
          <Route path="/delivery-failures" element={<ProtectedRoute><DeliveryFailures /></ProtectedRoute>} />
          <Route path="/migrate-whatsapp" element={<ProtectedRoute><MigrateWhatsApp /></ProtectedRoute>} />
          <Route path="/data-export" element={<ProtectedRoute><DataExport /></ProtectedRoute>} />
          <Route path="/data-health-check" element={<ProtectedRoute><DataHealthCheck /></ProtectedRoute>} />
          <Route path="/tariff-management" element={<ProtectedRoute><TariffManagement /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;